import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Settings, Lock, CreditCard, Layers, Plus } from 'lucide-react';
import { playlists as catalog } from '../data/tracks';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlaylists } from '../hooks/useUserPlaylists';
import { useLikedPlaylists } from '../hooks/useLikedPlaylists';
import { useUIStore } from '../stores/uiStore';
import { capture } from '../lib/posthog';
import { KEYS } from '../lib/onboardingState';
import { getSuggestedPlaylists, businessTypeLabel } from '../lib/getSuggestedPlaylists';
import PlaylistCover from './PlaylistCover';

function Sidebar({
  currentPlaylist,
  setCurrentPlaylist,
  onNavClick,
}) {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { playlists: userCreated, create } = useUserPlaylists();
  const { liked: likedPlaylistIds, toggle: toggleLikedPlaylist } = useLikedPlaylists();

  // Read businessType from onboarding localStorage (set by Onboarding.jsx)
  const businessType = useMemo(() => {
    try {
      const raw = localStorage.getItem(KEYS.ONBOARDED);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed.businessType : null;
    } catch {
      return null;
    }
  }, []);
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/' && location.pathname.startsWith(path));

  const handleNavClick = () => {
    if (onNavClick) onNavClick();
  };

  // Resolve liked default playlist IDs to full playlist objects
  const likedDefaults = useMemo(
    () => [...likedPlaylistIds].map(id => catalog.find(p => p.id === id)).filter(Boolean),
    [likedPlaylistIds]
  );

  // Combined: liked catalog playlists first, then user-created
  const yourPlaylists = useMemo(
    () => [...likedDefaults, ...userCreated],
    [likedDefaults, userCreated]
  );

  // B.1 — Suggested playlists for user's businessType (non-liked, non-created)
  const suggestedPlaylists = useMemo(
    () => getSuggestedPlaylists({
      businessType,
      likedPlaylistIds,
      userPlaylists: userCreated,
    }),
    [businessType, likedPlaylistIds, userCreated]
  );

  const handleCreateCTA = () => {
    capture('playlist_create_cta_clicked', { source: 'sidebar-top' });
    setShowCreateInput(true);
    setNewPlaylistName('');
    if (onNavClick) onNavClick();
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    const name = newPlaylistName.trim();
    if (!name) return;
    create(name);
    setShowCreateInput(false);
    setNewPlaylistName('');
  };

  const handleCreateKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowCreateInput(false);
      setNewPlaylistName('');
    }
  };

  return (
    <div className="sidebar bg-bg-sidebar text-text-primary">
      <Link to="/" className="sidebar-logo nav-logo" aria-label="Accueil Melodiza">
        <span className="logo-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M3 18V6l8-4v16l-8-4z" fill="#fbf8f4" opacity="0.95" />
            <path d="M11 2l10 5v10l-10 5V2z" fill="#fbf8f4" opacity="0.6" />
          </svg>
        </span>
        <span className="logo-text">
          melo<span className="logo-accent">diza</span>
        </span>
        <span className="logo-bars" aria-hidden="true">
          <i></i><i></i><i></i><i></i><i></i>
        </span>
      </Link>

      <nav className="sidebar-nav" data-tour="sidebar-nav" aria-label="Navigation principale">
        <Link
          to="/"
          className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={location.pathname === '/' ? 'page' : undefined}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.5 3.247a1 1 0 00-1 0L4 7.577V20h4.5v-6a1 1 0 011-1h5a1 1 0 011 1v6H20V7.577l-7.5-4.33z" />
          </svg>
          <span>Accueil</span>
        </Link>
        <Link
          to="/explorer"
          className={`nav-item ${isActive('/explorer') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={isActive('/explorer') ? 'page' : undefined}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10.533 1.279l-5.18 5.18a3 3 0 000 4.243l5.18 5.18a3 3 0 004.243 0l5.18-5.18a3 3 0 000-4.243l-5.18-5.18a3 3 0 00-4.243 0z" />
          </svg>
          <span>Explorer</span>
        </Link>
        <Link
          to="/library"
          className={`nav-item ${isActive('/library') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={isActive('/library') ? 'page' : undefined}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <span>Ma bibliothèque</span>
        </Link>
        <Link
          to="/plans"
          className={`nav-item ${isActive('/plans') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={location.pathname === '/plans' ? 'page' : undefined}
        >
          <CreditCard width="24" height="24" />
          <span>Mon abonnement</span>
        </Link>
      </nav>

      <div className="sidebar-divider"></div>

      <div className="sidebar-section-title">Mon commerce</div>
      <nav className="sidebar-nav" aria-label="Mon commerce">
        <Link
          to="/employee"
          className={`nav-item ${isActive('/employee') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={isActive('/employee') ? 'page' : undefined}
        >
          <Lock width="24" height="24" />
          <span>Mode Employé</span>
        </Link>
        <Link
          to="/schedule"
          className={`nav-item ${isActive('/schedule') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={isActive('/schedule') ? 'page' : undefined}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm1-13h-2v6l5.25 3.15.75-1.23-4-2.42V7z" />
          </svg>
          <span>Programmation</span>
        </Link>
        <Link
          to="/zones"
          className={`nav-item ${isActive('/zones') ? 'active' : ''}`}
          onClick={handleNavClick}
          aria-current={isActive('/zones') ? 'page' : undefined}
        >
          <Layers width="24" height="24" />
          <span>Zones</span>
        </Link>
      </nav>

      <div className="sidebar-divider"></div>

      <div className="sidebar-playlists">
        {/* CTA Créer — TOP per plan B.0 */}
        {showCreateInput ? (
          <form className="sidebar-create-form" onSubmit={handleCreateSubmit}>
            <input
              type="text"
              className="sidebar-create-input"
              placeholder="Nom de la playlist…"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              autoFocus
              aria-label="Nom de la nouvelle playlist"
            />
            <button type="submit" className="sidebar-create-confirm" aria-label="Créer la playlist">✓</button>
            <button type="button" className="sidebar-create-cancel" onClick={() => setShowCreateInput(false)} aria-label="Annuler">✕</button>
          </form>
        ) : (
          <button
            className="sidebar-section-cta"
            onClick={handleCreateCTA}
            data-tour="sidebar-create-cta"
            aria-label="Créer une nouvelle playlist"
          >
            <Plus size={18} aria-hidden="true" />
            <span>Créer une nouvelle playlist</span>
          </button>
        )}

        {/* Section Vos playlists (liked + created) */}
        <div className="sidebar-section-title" id="sidebar-your-playlists-label">Vos playlists</div>

        {yourPlaylists.length === 0 ? (
          <div className="sidebar-empty-state" role="status">
            <p>Aucune playlist pour l&apos;instant.</p>
            <Link to="/library" onClick={handleNavClick} className="sidebar-discover-link">
              Découvrir les playlists Melodiza →
            </Link>
          </div>
        ) : (
          <nav aria-labelledby="sidebar-your-playlists-label">
            {yourPlaylists.map((pl) => (
              <Link
                key={pl.id}
                to={`/playlist/${pl.id}`}
                className={`playlist-item ${currentPlaylist?.id === pl.id ? 'active' : ''}`}
                aria-current={currentPlaylist?.id === pl.id ? 'page' : undefined}
                onClick={() => {
                  if (setCurrentPlaylist) setCurrentPlaylist(pl);
                  handleNavClick();
                }}
              >
                <PlaylistCover
                  cover={pl.cover}
                  name={pl.name}
                  icon={pl.icon}
                  size={32}
                />
                <div className="playlist-info">
                  <span className="playlist-name">{pl.name}</span>
                  {pl.description && <span className="playlist-desc">{pl.description}</span>}
                </div>
              </Link>
            ))}
          </nav>
        )}
        {/* B.1 — Suggested section (only shows when there are suggestions) */}
        {suggestedPlaylists.length > 0 && (
          <>
            <div className="sidebar-divider"></div>
            <div className="sidebar-section-title">
              Suggérées pour {businessTypeLabel(businessType)}
            </div>
            {suggestedPlaylists.map(pl => (
              <div key={pl.id} className="suggested-playlist-row">
                <Link
                  to={`/playlist/${pl.id}`}
                  className={`playlist-item suggested-playlist-item ${currentPlaylist?.id === pl.id ? 'active' : ''}`}
                  onClick={() => {
                    if (setCurrentPlaylist) setCurrentPlaylist(pl);
                    handleNavClick();
                  }}
                >
                  <PlaylistCover
                    cover={pl.cover}
                    name={pl.name}
                    icon={pl.icon}
                    size={32}
                  />
                  <div className="playlist-info">
                    <span className="playlist-name">{pl.name}</span>
                    {pl.description && <span className="playlist-desc">{pl.description}</span>}
                  </div>
                </Link>
                <button
                  className="suggested-add-btn"
                  onClick={() => toggleLikedPlaylist(pl.id, 'sidebar-suggestion')}
                  aria-label={`Ajouter ${pl.name} à vos playlists`}
                  title="Ajouter à vos playlists"
                >
                  <Plus size={14} aria-hidden="true" />
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="sidebar-bottom">
        <nav className="sidebar-footer-nav" aria-label="Compte">
          <Link
            to="/certificate"
            className={`nav-item nav-item-compact ${isActive('/certificate') ? 'active' : ''}`}
            onClick={handleNavClick}
            aria-current={isActive('/certificate') ? 'page' : undefined}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
            </svg>
            <span>Certificat SACEM</span>
          </Link>
          <Link
            to="/settings"
            className={`nav-item nav-item-compact ${isActive('/settings') ? 'active' : ''}`}
            onClick={handleNavClick}
            aria-current={isActive('/settings') ? 'page' : undefined}
          >
            <Settings width="18" height="18" />
            <span>Paramètres</span>
          </Link>
        </nav>
        <div className="sidebar-badge">
          <span className="badge-text">✓ Sans SACEM</span>
        </div>
        {user && !user.isGuest && (
          <div className="sidebar-user">
            <span className="user-email">{user.email}</span>
            <button onClick={signOut} className="btn-logout" aria-label="Se déconnecter de votre compte">
              Se déconnecter
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
